import { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Heading,
  Link,
  Text,
} from '@podiumhq/design-system';

import PodiumLogo from 'components/PodiumLogo';
import EmailField from 'components/Trials/components/FormFields/EmailField';
import PasswordField from 'components/Trials/components/FormFields/PasswordField';
import { TrialDisclaimer } from 'components/Trials/layout';
import { subheadingStyles } from 'components/Trials/styles';
import { useTrialsContext } from 'contexts/TrialsContextProvider';
import { PODIUM_LOGIN_URL } from 'routes/constants';

import { CONTINUE_WITH_EMAIL, START_TRIAL_BUTTON_LABEL } from '../../constants';
import SignInWithGoogle from 'components/SignInWithGoogle';
import SignInWithApple from 'components/SignInWithApple';

const SignupContent = ({ isSubmitting, handleSubmit }) => {
  const [emailSelected, setEmailSelected] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const { isMobile } = useTrialsContext() || {};
  const marginValue = isMobile ? 'auto' : null;

  const onFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit();
  };

  return (
    <>
      {!isMobile && (
        <PodiumLogo icon={true} ml="32px" mt="32px" {...logoStyles} />
      )}

      <Box
        display="flex"
        height={isMobile ? '96vh' : '100vh'}
        justifyContent="center"
        marginLeft={marginValue}
        marginRight={marginValue}
        padding={isMobile ? '4vh 0vh 0vh 0vh' : '22vh'}
        position="relative"
        width={isMobile ? '100%' : '50%'}
      >
        {isMobile && <PodiumLogo icon={true} ml="0" mt="0" {...logoStyles} />}

        <Box maxWidth="350px" minWidth={isMobile ? null : '350px'}>
          <Heading
            color="black.base"
            fontSize="2xl"
            fontWeight="semibold"
            padding={isMobile ? '48px 0px 0px 0px' : '24px 0'}
          >
            Give Podium a try. It’s free.
          </Heading>

          <Text {...subheadingStyles}>
            Grow with tools for texting customers, getting reviews, and making
            sales. No credit card required.
          </Text>

          <form onSubmit={onFormSubmit}>
            <>
              <SignInWithGoogle onLogin={handleSubmit} />
              <SignInWithApple onLogin={handleSubmit} />
              <Divider />
            </>

            {!emailSelected && (
              <Text
                color="black.lightest"
                fontSize="md"
                fontWeight="normal"
                marginLeft="auto"
                marginRight="auto"
                marginTop="24px"
                minWidth="220px"
                textAlign="center"
              >
                You can also continue with{' '}
                <Link
                  color="blue.base"
                  onClick={() => setEmailSelected(true)}
                  target="_blank"
                  id="sign-in-email-link"
                >
                  email
                </Link>
                .
              </Text>
            )}
            {emailSelected && (
              <>
                <Text
                  marginBottom="4px"
                  marginTop="24px"
                  fontSize="md"
                  fontWeight="medium"
                >
                  Email
                </Text>
                <EmailField
                  isSubmitting={isSubmitting}
                  placeholder="Enter your email address..."
                />
                {!showPasswordInput && (
                  <Button
                    data-e2e="continue-with-email-btn"
                    display="block"
                    fillColor="blue.base"
                    fontSize="lg"
                    fontWeight="semibold"
                    id="trials-account-info-next-clicked"
                    mb="32px"
                    size="lg"
                    width="100%"
                    onClick={() => setShowPasswordInput(!showPasswordInput)}
                  >
                    {CONTINUE_WITH_EMAIL}
                  </Button>
                )}
              </>
            )}

            {showPasswordInput && (
              <>
                <Text marginBottom="4px" fontSize="md" fontWeight="medium">
                  Password
                </Text>
                <PasswordField
                  fillColor="black.lighter"
                  placeholder="Create a password"
                />
                <Button
                  data-e2e="start-free-trial-btn"
                  display="block"
                  fillColor="blue.base"
                  fontSize="lg"
                  fontWeight="semibold"
                  id="trials-account-info-next-clicked"
                  mb="32px"
                  size="lg"
                  width="100%"
                  type="submit"
                >
                  {START_TRIAL_BUTTON_LABEL}
                </Button>
              </>
            )}

            <TrialDisclaimer
              linkProps={{ textDecoration: 'none', color: 'blue.base' }}
              mb="32px"
              mt="50px"
              textAlign="center"
            />

            <Text
              color="black.lightest"
              fontSize="md"
              fontWeight="normal"
              marginLeft="auto"
              marginRight="auto"
              width="215px"
            >
              Already have an account?{' '}
              <Link color="blue.base" href={PODIUM_LOGIN_URL} target="_blank">
                Log in
              </Link>
            </Text>
          </form>
        </Box>
      </Box>
    </>
  );
};

const logoStyles = { position: 'absolute', zIndex: '1' };

export default SignupContent;
